/**
 * 채팅 세션 관리 서비스
 * App.js에서 세션 관련 로직을 분리하여 관리
 */

/**
 * 채팅 세션 관리 클래스
 */
export class ChatSessionManager {
  constructor(config) {
    this.apiBaseUrl = config.apiBaseUrl;
    this.streamSessionUrl = config.streamSessionUrl;
    this.callbacks = config.callbacks || {};
    this.initialMessage = config.initialMessage;
  }

  /**
   * 새로운 채팅 세션 생성
   * @param {string} title - 세션 제목 (선택적)
   * @returns {Promise<number|null>} 생성된 세션 ID 또는 null (실패시)
   */
  async createSession(title = null) {
    try {
      // 로딩 상태 설정 (UI 피드백용)
      if (this.callbacks.onLoadingChange) {
        this.callbacks.onLoadingChange(true);
      }
      
      if (this.callbacks.onErrorChange) {
        this.callbacks.onErrorChange('');
      }
      
      // 시각화 데이터 초기화 (새 대화 시작 시)
      if (this.callbacks.onVisualizationsChange) {
        this.callbacks.onVisualizationsChange([]);
      }

      // 세션 제목 생성 (제공되지 않은 경우)
      const sessionTitle = title || '새 대화 ' + new Date().toLocaleString();

      // 새로운 세션 생성 전용 엔드포인트 사용
      const res = await fetch(`${this.apiBaseUrl}/chat/create-session/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // 쿠키를 포함하여 요청 전송
        body: JSON.stringify({
          title: sessionTitle
        })
      });
      
      // 응답 상태 코드 로깅
      // HTTP 오류 응답인 경우, 응답 본문 로깅 시도
      if (!res.ok) {
        let errorBody = '';
        try {
          errorBody = await res.text();
        } catch (textError) {
        }
        throw new Error(`세션 생성 실패: ${res.status} ${res.statusText} ${errorBody}`);
      }
      
      // 성공 응답인 경우
      try {
        const data = await res.json();
        // 유효한 ID가 있는지 확인
        if (!data || !data.id) {
          throw new Error("세션 ID가 응답에 없습니다");
        }
        
        // 세션 ID를 정수로 변환
        const sessionId = Number(data.id);
        // 세션 목록 갱신 (옵션)
        if (this.callbacks.onSessionsUpdate && this.callbacks.getCurrentSessions) {
          const currentSessions = this.callbacks.getCurrentSessions();
          if (Array.isArray(currentSessions)) {
            this.callbacks.onSessionsUpdate(prev => [data, ...prev]);
          }
        }
        
        // 세션 ID 설정 (현재 활성 세션으로 지정)
        if (this.callbacks.onSessionIdChange) {
          this.callbacks.onSessionIdChange(sessionId);
        }
        
        // 채팅 기록 초기화 및 초기 메시지 추가
        if (this.callbacks.onChatHistoryChange && this.initialMessage) {
          this.callbacks.onChatHistoryChange([this.initialMessage]);
        }
        
        // 로딩 상태 해제
        if (this.callbacks.onLoadingChange) {
          this.callbacks.onLoadingChange(false);
        }
        
        // 성공 시 세션 ID 반환
        return sessionId;
      } catch (parseError) {
        throw new Error(`응답 데이터 처리 중 오류: ${parseError.message}`);
      }
    } catch (error) {
      if (this.callbacks.onErrorChange) {
        this.callbacks.onErrorChange(`세션 생성 중 오류가 발생했습니다: ${error.message}`);
      }
      if (this.callbacks.onLoadingChange) {
        this.callbacks.onLoadingChange(false);
      }
      return null;
    }
  }

  /**
   * 세션 스트리밍 URL 생성
   * @returns {string} 스트리밍 URL
   */
  getSessionStreamUrl() {
    return this.streamSessionUrl;
  }

  /**
   * 세션 유효성 검사 및 필요시 새 세션 생성
   * @param {number|null} currentSessionId - 현재 세션 ID
   * @returns {Promise<number|null>} 유효한 세션 ID 또는 null (실패시)
   */
  async ensureValidSession(currentSessionId) {
    // 세션 ID가 없으면 새로 생성
    if (!currentSessionId) {
      const newSessionId = await this.createSession();
      if (!newSessionId) {
        throw new Error('세션을 생성할 수 없습니다.');
      }
      return newSessionId;
    }
    
    // 기존 세션 ID가 있으면 그대로 사용
    return currentSessionId;
  }

  /**
   * 세션 스트리밍 요청 URL 생성
   * @param {number} sessionId - 세션 ID
   * @param {string} message - 전송할 메시지
   * @param {string} model - 사용할 모델명 (선택적)
   * @param {boolean} devMode - 개발자 모드 여부 (선택적)
   * @returns {string} 완전한 요청 URL
   */
  buildStreamingRequestUrl(sessionId, message, model = null, devMode = false) {
    const chatUrl = this.getSessionStreamUrl();
    const params = new URLSearchParams();
    params.append('message', message);
    
    // 모델 정보가 있으면 추가
    if (model) {
      params.append('model', model);
    }
    
    // 개발자 모드 정보 추가
    if (devMode) {
      params.append('dev_mode', 'true');
    }
    
    return `${chatUrl}${sessionId}/?${params.toString()}`;
  }

  /**
   * 세션 매니저 정리
   */
  cleanup() {
    // 필요한 경우 정리 작업 수행
  }
}

/**
 * 세션 매니저 인스턴스 생성 헬퍼 함수
 * @param {Object} config - 설정 객체
 * @param {string} config.apiBaseUrl - API 기본 URL
 * @param {string} config.streamSessionUrl - 스트리밍 세션 URL
 * @param {Object} config.callbacks - 콜백 함수들
 * @param {Object} config.initialMessage - 초기 메시지
 * @returns {ChatSessionManager} 세션 매니저 인스턴스
 */
export const createChatSessionManager = (config) => {
  return new ChatSessionManager(config);
};

/**
 * 단순한 세션 URL 생성 함수 (기존 getChatUrl 대체)
 * @param {string} streamSessionUrl - 스트리밍 세션 기본 URL
 * @returns {string} 스트리밍 URL
 */
export const getChatUrl = (streamSessionUrl) => {
  // 항상 세션 스트리밍 URL 사용
  return streamSessionUrl;
};

/**
 * 세션 생성 함수 (함수형 접근)
 * @param {string} apiBaseUrl - API 기본 URL
 * @param {string} title - 세션 제목
 * @returns {Promise<Object>} 세션 생성 응답
 */
export const createChatSession = async (apiBaseUrl, title = null) => {
  const sessionTitle = title || '새 대화 ' + new Date().toLocaleString();
  
  const res = await fetch(`${apiBaseUrl}/chat/create-session/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      title: sessionTitle
    })
  });
  
  if (!res.ok) {
    const errorBody = await res.text();
    throw new Error(`세션 생성 실패: ${res.status} ${res.statusText} ${errorBody}`);
  }
  
  return await res.json();
};

/**
 * 세션 유효성 검사 함수
 * @param {number|null} sessionId - 검사할 세션 ID
 * @param {Function} createIfMissing - 세션이 없을 때 생성할 함수
 * @returns {Promise<number|null>} 유효한 세션 ID 또는 null
 */
export const validateSession = async (sessionId, createIfMissing = null) => {
  if (!sessionId && createIfMissing) {
    return await createIfMissing();
  }
  return sessionId;
}; 